import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Check,
  User,
  Building,
  MapPin,
  CreditCard,
  FileText,
  AlertCircle,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Upload,
  Users,
} from "lucide-react";
import useEmployeeMasters from "../../hooks/useEmployeeMasters";
import SearchableSelect from "../common/SearchableSelect";
import {
  buildEmployeeMasterPayload,
  uploadPendingDocuments,
} from "../../utils/employeeMasterPayload";
import {
  TYPE_CODE_MAP,
  EMPLOYEE_TYPE_FORM_OPTIONS,
  displayEmployeeType,
} from "../../utils/employeeTypeLabels";
import { buildApiUrl } from '../../utils/apiSetup';

// Form Field Component - defined outside main component to prevent re-renders
const FormField = ({
  label,
  field,
  type = "text",
  required = false,
  options = null,
  placeholder = "",
  rows = null,
  disabled = false,
  helpText = null,
  formData,
  errors,
  onChange,
  previewMode,
}) => {
  const hasError = errors?.[field];
  const value = formData?.[field] || "";

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {previewMode ? (
        <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 min-h-[38px] flex items-center">
          {(options
            ? options.find((option) => String(option.value) === String(value))
                ?.label
            : value) || <span className="text-gray-400">Not provided</span>}
        </div>
      ) : options ? (
        <select
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? "border-red-300 bg-red-50" : "border-gray-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : rows ? (
        <textarea
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? "border-red-300 bg-red-50" : "border-gray-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? "border-red-300 bg-red-50" : "border-gray-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        />
      )}

      {helpText && !hasError && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {hasError && (
        <p className="text-red-600 text-sm flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors[field]}
        </p>
      )}
    </div>
  );
};

// Section Header Component - defined outside main component to prevent re-renders
const SectionHeader = ({
  title,
  icon: Icon,
  color,
  section,
  expanded,
  onToggle,
  children,
}) => (
  <div className={`border border-gray-200 rounded-lg mb-4 ${expanded ? 'overflow-visible' : 'overflow-hidden'}`}>
    <button
      type="button"
      onClick={() => onToggle(section)}
      className={`w-full flex items-center justify-between p-4 ${color} text-white hover:opacity-90 transition-opacity`}
    >
      <div className="flex items-center">
        <Icon className="w-5 h-5 mr-2" />
        <span className="font-medium">{title}</span>
      </div>
      {expanded ? (
        <ChevronUp className="w-5 h-5" />
      ) : (
        <ChevronDown className="w-5 h-5" />
      )}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={expanded ? "overflow-visible" : "overflow-hidden"}
        >
          <div className="p-4 bg-white rounded-b-lg">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const EmployeeCreationForm = ({
  isOpen,
  onClose,
  onSuccess,
  editEmployee = null,
  viewMode = false,
  editMode = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewMode, setPreviewMode] = useState(viewMode);
  const {
    masters,
    loading: mastersLoading,
    searchManagers,
    fetchDesignations,
    fetchDepartmentHeads,
  } = useEmployeeMasters();
  const [departments, setDepartments] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [departmentDesignations, setDepartmentDesignations] = useState([]);
  const [departmentHeadsList, setDepartmentHeadsList] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [docChecklist, setDocChecklist] = useState(null);
  const [scalesList, setScalesList] = useState([]);
  const isPfAccountInitialized = editEmployee?.status === "active";

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    organization: true,
    orgGroups: true,
    personal: true,
    address: true,
    bank: true,
    documents: true,
  });

  // Form data
  const [formData, setFormData] = useState({
    // Basic Information
    emp_code: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    department_id: "",
    payroll_head_id: "",
    personal_email: "",
    official_email: "",
    phone: "",
    alternate_phone: "",
    employee_type: "permanent",
    employee_type_master_id: "",

    // Organization Details
    join_date: "",
    department_id: "",
    designation_type: "",
    designation_id: "",
    emp_desig: "",
    scale_id: "",
    basic_pay: "",
    reporting_manager_id: "",
    supervisor_id: "",
    functional_manager_id: "",
    office_location_id: "",
    shift_id: "",
    shift_group_id: "",
    roster_group_id: "",
    payroll_group_id: "",
    leave_policy_master_id: "",
    attendance_policy_master_id: "",
    employment_status: "probation",
    submit_for_approval: false,

    // Personal Details
    father_name: "",
    mother_name: "",
    date_of_birth: "",
    sex: "",
    marital_status: "",
    blood_group: "",
    is_disabled: "",

    // Address Details
    current_address: "",
    permanent_address: "",
    city: "",
    state: "",
    pin_code: "",
    emergency_contact_name: "",
    emergency_contact_number: "",
    emergency_contact_relationship: "",

    // Bank & Tax Details
    pan_number: "",
    aadhaar_number: "",
    pension_scheme_type: "",
    pf_number: "",
    pran_number: "",
    hba_id: "",
    opening_pf_balance: "",
    opening_vpf_balance: "",
    opening_pf_balance_as_on: "",
    is_eligible_washing_allowance: false,
    is_eligible_family_planning_allowance: false,
    is_eligible_conveyance_allowance: false,
    bank_name: "",
    bank_id: "",
    bank_account_number: "",
    bank_ifsc_code: "",
    bank_branch: "",
    bank_branch_code: "",

    // Other Documents
    other_documents: [],
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  const employeeTypeOptions = masters?.employeeTypes?.length
    ? masters.employeeTypes.map((t) => ({
        value: TYPE_CODE_MAP[t.code] || t.code?.toLowerCase() || "permanent",
        label: t.label,
        masterId: t.value,
      }))
    : EMPLOYEE_TYPE_FORM_OPTIONS;

  const designationTypeOptions = [
    { value: "PROMOTIONAL", label: "Promotional" },
    { value: "ISOLATED", label: "Isolated" },
  ];

  useEffect(() => {
    if (!isOpen || !masters?.departments) return;
    setDepartments(
      masters.departments.map((d) => ({
        id: d.value,
        name: d.label,
        code: d.code,
      })),
    );
  }, [isOpen, masters]);

  const loadManagers = useCallback(
    async (q = "") => {
      const rows = await searchManagers(q);
      setManagerOptions(
        rows.map((m) => ({
          value: m.id,
          label: `${m.name} (${m.emp_code})`,
          subLabel: m.department || "",
        })),
      );
    },
    [searchManagers],
  );

  useEffect(() => {
    if (isOpen) loadManagers("");
  }, [isOpen, loadManagers]);

  useEffect(() => {
    let isMounted = true;
    let requestId = 0;

    const fetchDesignationsByType = async () => {
      if (!isOpen) return;
      if (!formData.department_id || !formData.designation_type) {
        setDepartmentDesignations([]);
        return;
      }

      requestId += 1;
      const currentRequestId = requestId;

      try {
        const data = await fetchDesignations(
          formData.department_id,
          formData.designation_type,
        );
        if (isMounted && currentRequestId === requestId) {
          setDepartmentDesignations(data || []);
        }
      } catch (error) {
        console.error("Error fetching designations:", error);
        if (isMounted && currentRequestId === requestId) {
          setDepartmentDesignations([]);
        }
      }
    };

    fetchDesignationsByType();

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    formData.department_id,
    formData.designation_type,
    fetchDesignations,
  ]);

  // Log formData on every render to trace state changes
  useEffect(() => {
    console.log("formData render:", formData);
  }, [formData]);

  const designationOptions = useMemo(() => {
    const options = departmentDesignations.map((d) => ({
      value: d.value ?? d.id,
      label: d.label ?? d.title,
      grade: d.grade ?? d.grade_name,
      department_id: d.department_id,
    }));
    console.log("designationOptions computed:", options);
    return options;
  }, [departmentDesignations]);

  useEffect(() => {
    let isMounted = true;
    let requestId = 0;

    const fetchHeadsForDepartment = async () => {
      if (!isOpen) return;
      if (!formData.department_id) {
        setDepartmentHeadsList([]);
        return;
      }

      requestId += 1;
      const currentRequestId = requestId;

      try {
        const data = await fetchDepartmentHeads(formData.department_id);
        if (isMounted && currentRequestId === requestId) {
          setDepartmentHeadsList(data || []);
        }
      } catch (error) {
        console.error("Error fetching department heads:", error);
        if (isMounted && currentRequestId === requestId) {
          setDepartmentHeadsList([]);
        }
      }
    };

    fetchHeadsForDepartment();

    return () => {
      isMounted = false;
    };
  }, [isOpen, formData.department_id, fetchDepartmentHeads]);

  const [banksList, setBanksList] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchScales = async () => {
      try {
        const res = await axios.get("/v1/designations/scales");
        if (Array.isArray(res.data)) {
          setScalesList(res.data);
        }
      } catch (err) {
        console.error("Error loading scales from scale master:", err);
      }
    };
    const fetchBanks = async () => {
      try {
        const res = await axios.get("/v1/banks");
        if (Array.isArray(res.data)) {
          setBanksList(res.data);
        }
      } catch (err) {
        console.error("Error loading banks from bank master:", err);
      }
    };
    fetchScales();
    fetchBanks();
  }, [isOpen]);

  const scaleOptions = useMemo(() => {
    const source = scalesList.length > 0 ? scalesList : (masters?.scales || []);
    return source.map((s) => {
      const code = s.code;
      const name = s.name;
      const minB = s.min_basic != null ? Math.round(s.min_basic) : null;
      const maxB = s.max_basic != null ? Math.round(s.max_basic) : null;

      let codeOrName = code || name;
      if (code && name && name.toLowerCase().includes(code.toLowerCase())) {
        codeOrName = code;
      }
      const label = (minB !== null || maxB !== null)
        ? `${codeOrName} ( ${minB ?? 0} - ${maxB ?? 0} )`
        : codeOrName;

      return {
        value: s.id,
        label: label,
        code: code,
        name: name,
        min_basic: s.min_basic != null ? s.min_basic : null,
        max_basic: s.max_basic != null ? s.max_basic : null,
      };
    });
  }, [scalesList, masters?.scales]);

  const selectedScaleObj = useMemo(() => {
    if (!formData.scale_id) return null;
    return scaleOptions.find(
      (s) => String(s.value) === String(formData.scale_id)
    );
  }, [formData.scale_id, scaleOptions]);

  const basicRangeHelpText = useMemo(() => {
    if (!selectedScaleObj) return "";
    const minB = selectedScaleObj.min_basic;
    const maxB = selectedScaleObj.max_basic;
    if (minB != null && maxB != null) {
      return `In Range ${minB} - ${maxB}`;
    }
    return "";
  }, [selectedScaleObj]);

  // Document type code mapping for mandatory documents
  const DOCUMENT_CODE_MAPPING = {
    "Aadhaar Card": "AADHAAR",
    "PAN Card": "PAN",
    "Appointment Memo": "APPOINTMENT",
    "Medical Certificate": "MEDICAL",
    "Education Certificate": "DEGREE",
    "Police Verification": "POLICE_VERIFICATION",
    "Reference Letter": "REFERENCE_LETTER",
    "Passbook / Checkbook": "PASSBOOK",
  };

const mandatoryDocTypes = useMemo(() => {
  const docTypeMap = new Map();

  if (masters?.documentTypes?.length > 0) {
    console.log("Sample document type from masters:", masters.documentTypes[0]);
    const filtered = masters.documentTypes
      .filter((docType) => docType.document_group === "registration");
    console.log("Filtered registration documents:", filtered);
    const mapped = filtered
      .map((docType) => {
        const label = docType.label || docType.name;
        const code = docType.code;
        const result = {
          value: docType.id,
          label,
          code,
          mandatory: Boolean(docType.mandatory || docType.is_mandatory),
        };
        console.log("Mapped single docType:", result);
        return result;
      })
      .filter(Boolean);
    console.log("Mapped document types:", mapped);
    return mapped;
  }
  return [];
}, [masters?.documentTypes]);

  useEffect(() => {
    if (!isOpen || !mandatoryDocTypes.length) {
      setDocChecklist(null);
      return;
    }
    const uploadedTypes = new Set(
      pendingDocuments.map((d) => Number(d.document_type_id)),
    );
    setDocChecklist({
      items: mandatoryDocTypes.map((t) => ({
        document_type_id: t.value,
        name: t.label,
        uploaded: uploadedTypes.has(Number(t.value)),
      })),
      complete: mandatoryDocTypes.filter((t) => t.mandatory).every((t) =>
        uploadedTypes.has(Number(t.value)),
      ),
    });
  }, [isOpen, mandatoryDocTypes, pendingDocuments]);

  useEffect(() => {
    if (isOpen) {
      // Always clear success/error messages when modal opens
      setSuccess("");
      setError("");
      setErrors({});
      setPreviewMode(viewMode);

      if (editEmployee) {
        setFormData({
          ...editEmployee,
          bank_name:
            editEmployee.bank_details?.bank_name ||
            editEmployee.bank_name ||
            "",
          bank_id:
            editEmployee.bank_details?.bank_id ||
            editEmployee.bank_id ||
            "",
          bank_account_number:
            editEmployee.bank_details?.bank_account_number ||
            editEmployee.bank_account_number ||
            "",
          bank_account_name:
            editEmployee.bank_details?.bank_account_name ||
            editEmployee.bank_account_name ||
            "",
          bank_ifsc_code:
            editEmployee.bank_details?.bank_ifsc_code ||
            editEmployee.bank_ifsc_code ||
            "",
          bank_branch:
            editEmployee.bank_details?.bank_branch ||
            editEmployee.bank_branch ||
            "",
          bank_branch_code:
            editEmployee.bank_details?.bank_branch_code ||
            editEmployee.bank_branch_code ||
            "",
          reporting_manager_id:
            editEmployee.hierarchy?.reporting_manager_id ||
            editEmployee.reporting_manager_id ||
            "",
          supervisor_id:
            editEmployee.hierarchy?.supervisor_id ||
            editEmployee.supervisor_id ||
            "",
          functional_manager_id:
            editEmployee.hierarchy?.functional_manager_id ||
            editEmployee.functional_manager_id ||
            "",
          department_head_id:
            editEmployee.hierarchy?.department_head_id ||
            editEmployee.department_head_id ||
            "",
          business_unit:
            editEmployee.hierarchy?.business_unit ||
            editEmployee.business_unit ||
            "",
          hospital_branch:
            editEmployee.hierarchy?.hospital_branch ||
            editEmployee.hospital_branch ||
            "",
          work_location:
            editEmployee.hierarchy?.work_location ||
            editEmployee.work_location ||
            "",
          opening_pf_balance: editEmployee.opening_pf_balance ?? "",
          opening_vpf_balance: editEmployee.opening_vpf_balance ?? "",
          opening_pf_balance_as_on: editEmployee.opening_pf_balance_as_on ?? "",
          other_documents: editEmployee.other_documents || [],
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, editEmployee, viewMode]);

  const resetForm = () => {
    setFormData({
      emp_code: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      personal_email: "",
      official_email: "",
      phone: "",
      alternate_phone: "",
      employee_type: "permanent",
      employee_type_master_id: "",
      hospital_role_id: "",
      join_date: "",
      department_id: "",
      designation_type: "",
      designation_id: "",
      emp_desig: "",
      scale_id: "",
      basic_pay: "",
      reporting_manager_id: "",
      supervisor_id: "",
      functional_manager_id: "",
      office_location_id: "",
      shift_id: "",
      shift_group_id: "",
      roster_group_id: "",
      payroll_group_id: "",
      leave_policy_master_id: "",
      attendance_policy_master_id: "",
      employment_status: "probation",
      submit_for_approval: false,
      father_name: "",
      mother_name: "",
      date_of_birth: "",
      sex: "",
      marital_status: "",
      blood_group: "",
      is_disabled: "",
      current_address: "",
      permanent_address: "",
      city: "",
      state: "",
      pin_code: "",
      emergency_contact_name: "",
      emergency_contact_number: "",
      emergency_contact_relationship: "",
      pan_number: "",
      aadhaar_number: "",
      pension_scheme_type: "",
      pf_number: "",
      pran_number: "",
      opening_pf_balance: "",
      opening_vpf_balance: "",
      opening_pf_balance_as_on: "",
      bank_name: "",
      bank_id: "",
      bank_account_number: "",
      bank_ifsc_code: "",
      bank_branch: "",
      bank_branch_code: "",
      other_documents: [],
    });
    setPendingDocuments([]);
    setErrors({});
    setError("");
    setSuccess("");
    setPreviewMode(false);
  };

  const fetchNextEmployeeCode = async (employeeType) => {
    try {
      const typeKey = employeeType?.includes("contract")
        ? "contractual"
        : employeeType?.includes("consult")
          ? "consultant"
          : "permanent";
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/next-code?employee_type=${typeKey}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        return data.emp_code;
      }
      return null;
    } catch (error) {
      console.error("Error fetching employee code:", error);
      return null;
    }
  };

  const handleEmployeeTypeChange = async (value) => {
    const selectedOption = employeeTypeOptions.find(
      (opt) => opt.value === value,
    );
    setFormData((prev) => ({
      ...prev,
      employee_type: value,
      employee_type_master_id: selectedOption?.masterId || "",
    }));

    if (selectedOption && !editEmployee) {
      const nextCode = await fetchNextEmployeeCode(value);
      if (nextCode) {
        setFormData((prev) => ({ ...prev, emp_code: nextCode }));
      }
    }

    if (errors.employee_type) {
      setErrors((prev) => ({ ...prev, employee_type: null }));
    }
  };

  const handleDepartmentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      department_id: value,
      designation_id: "",
      emp_desig: "",
      scale_id: "",
      payroll_head_id: "",
    }));
    setDepartmentDesignations([]);
    setDepartmentHeadsList([]);
    if (errors.department_id)
      setErrors((prev) => ({ ...prev, department_id: null }));
  };

  const handleDesignationTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      designation_type: value,
      designation_id: "",
      emp_desig: "",
      scale_id: "",
    }));
    setDepartmentDesignations([]);
    if (errors.designation_type)
      setErrors((prev) => ({ ...prev, designation_type: null }));
  };

  const handleDesignationChange = (designationId) => {
    const des = designationOptions.find(
      (d) => String(d.value) === String(designationId),
    );
    console.log("handleDesignationChange - designation object:", des);
    console.log("handleDesignationChange - designationId:", designationId);
    console.log("handleDesignationChange - des?.grade:", des?.grade);
    setFormData((prev) => {
      const payload = {
        ...prev,
        designation_id: designationId,
        emp_desig: des?.label || prev.emp_desig,
        scale_id: "",  // Reset scale_id when designation changes
      };
      console.log("handleDesignationChange - setFormData payload:", payload);
      return payload;
    });
    if (errors.designation_id)
      setErrors((prev) => ({ ...prev, designation_id: null }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handlePensionSchemeChange = (value) => {
    setFormData((prev) => {
      const updates = {
        ...prev,
        pension_scheme_type: value,
      };

      // Clear incompatible fields based on scheme type
      if (value === "GPF") {
        updates.pran_number = "";
      } else if (value === "NPS") {
        updates.pf_number = "";
        updates.opening_pf_balance = "";
        updates.opening_vpf_balance = "";
        updates.opening_pf_balance_as_on = "";
      }

      return updates;
    });

    if (errors.pension_scheme_type) {
      setErrors((prev) => ({
        ...prev,
        pension_scheme_type: null,
      }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.first_name) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.middle_name) {
      newErrors.middle_name = "Middle name is required";
    }
    if (!formData.last_name) {
      newErrors.last_name = "Last name is required";
    }
    if (!formData.personal_email) {
      newErrors.personal_email = "Personal email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personal_email)) {
      newErrors.personal_email = "Please enter a valid email address";
    }
    if (!formData.phone) {
      newErrors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.alternate_phone) {
      newErrors.alternate_phone = "Alternate mobile number is required";
    } else if (!/^\d{10}$/.test(formData.alternate_phone)) {
      newErrors.alternate_phone = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.join_date) {
      newErrors.join_date = "Join date is required";
    }
    if (!formData.department_id) {
      newErrors.department_id = "Department is required";
    }
    if (!formData.payroll_head_id) {
      newErrors.payroll_head_id = "Head is required";
    }
    if (!formData.designation_type) {
      newErrors.designation_type = "Designation type is required";
    }
    if (!formData.designation_id) {
      newErrors.designation_id = "Designation is required";
    }
    if (formData.scale_id) {
      if (!formData.basic_pay) {
        newErrors.basic_pay = "Basic pay is required";
      } else if (
        selectedScaleObj &&
        selectedScaleObj.min_basic != null &&
        selectedScaleObj.max_basic != null
      ) {
        const val = Number(formData.basic_pay);
        const minB = Number(selectedScaleObj.min_basic);
        const maxB = Number(selectedScaleObj.max_basic);
        if (isNaN(val) || val < minB || val > maxB) {
          newErrors.basic_pay = `Value must be within range ${minB} - ${maxB}`;
        }
      }
    }
    if (!formData.office_location_id) {
      newErrors.office_location_id = "Office location is required";
    }
    if (!formData.shift_id) {
      newErrors.shift_id = "Default shift is required";
    }
    if (!formData.father_name) {
      newErrors.father_name = "Father's name is required";
    }
    if (!formData.mother_name) {
      newErrors.mother_name = "Mother's name is required";
    }
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required";
    }
    if (!formData.sex) {
      newErrors.sex = "Gender is required";
    }
    if (!formData.marital_status) {
      newErrors.marital_status = "Marital status is required";
    }
    if (!formData.blood_group) {
      newErrors.blood_group = "Blood group is required";
    }
    if (!formData.is_disabled) {
      newErrors.is_disabled = "Person with disability is required";
    }
    if (!formData.current_address) {
      newErrors.current_address = "Current address is required";
    }
    if (!formData.permanent_address) {
      newErrors.permanent_address = "Permanent address is required";
    }
    if (!formData.city) {
      newErrors.city = "City is required";
    }
    if (!formData.state) {
      newErrors.state = "State is required";
    }
    if (!formData.pin_code) {
      newErrors.pin_code = "PIN code is required";
    } else if (!/^\d{6}$/.test(formData.pin_code)) {
      newErrors.pin_code = "Please enter a valid 6-digit PIN code";
    }
    if (!formData.emergency_contact_name) {
      newErrors.emergency_contact_name = "Emergency contact name is required";
    }
    if (!formData.emergency_contact_number) {
      newErrors.emergency_contact_number =
        "Emergency contact number is required";
    } else if (!/^\d{10}$/.test(formData.emergency_contact_number)) {
      newErrors.emergency_contact_number =
        "Please enter a valid 10-digit mobile number";
    }
    if (!formData.emergency_contact_relationship) {
      newErrors.emergency_contact_relationship = "Relationship is required";
    }
    if (!formData.pan_number) {
      newErrors.pan_number = "PAN number is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_number)) {
      newErrors.pan_number =
        "Please enter a valid PAN number (e.g., ABCDE1234F)";
    }
    if (!formData.aadhaar_number) {
      newErrors.aadhaar_number = "Aadhaar number is required";
    } else if (!/^\d{12}$/.test(formData.aadhaar_number)) {
      newErrors.aadhaar_number = "Please enter a valid 12-digit Aadhaar number";
    }
    if (!formData.pension_scheme_type) {
      newErrors.pension_scheme_type = "Pension scheme type is required";
    }
    if (!formData.bank_id) {
      newErrors.bank_id = "Bank selection is required";
    }
    if (!formData.bank_account_number) {
      newErrors.bank_account_number = "Account number is required";
    }
    if (!formData.bank_ifsc_code) {
      newErrors.bank_ifsc_code = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bank_ifsc_code)) {
      newErrors.bank_ifsc_code =
        "Please enter a valid IFSC code (e.g., SBIN0001234)";
    }
    if (!formData.bank_branch) {
      newErrors.bank_branch = "Branch is required";
    }
    if (!formData.bank_branch_code) {
      newErrors.bank_branch_code = "Branch code is required";
    }

    // Document validation - only mandatory documents are required
    if (!editEmployee) {
      const mandatoryDocIds = mandatoryDocTypes
        .filter((dt) => dt.mandatory)
        .map((dt) => Number(dt.value));
      const uploadedDocIds = pendingDocuments.map((d) => Number(d.document_type_id));
      
      const missingDocs = mandatoryDocIds.filter(
        (id) => !uploadedDocIds.includes(id)
      );
      
      if (missingDocs.length > 0) {
        const missingDocNames = mandatoryDocTypes
          .filter((dt) => missingDocs.includes(Number(dt.value)))
          .map((dt) => dt.label)
          .join(", ");
        newErrors.documents = `Please upload the following mandatory documents: ${missingDocNames}`;
      }
    }

    // Optional field validations
    if (
      formData.official_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.official_email)
    ) {
      newErrors.official_email = "Please enter a valid email address";
    }

    // Opening PF Balance validation (compulsory presence check)
    if (formData.pension_scheme_type === "GPF") {
      const opPfRaw = formData.opening_pf_balance;
      const opVpfRaw = formData.opening_vpf_balance;

      if (opPfRaw === "" || opPfRaw === null || opPfRaw === undefined) {
        newErrors.opening_pf_balance = "Opening PF Balance is required. Enter 0 if the employee has no opening PF balance.";
      } else {
        const opPfNum = parseFloat(opPfRaw);
        if (isNaN(opPfNum)) {
          newErrors.opening_pf_balance = "Opening PF Balance must be a valid number";
        } else if (opPfNum < 0) {
          newErrors.opening_pf_balance = "Opening PF Balance cannot be negative.";
        }
      }

      if (opVpfRaw === "" || opVpfRaw === null || opVpfRaw === undefined) {
        newErrors.opening_vpf_balance = "Opening VPF Balance is required. Enter 0 if the employee has no opening VPF balance.";
      } else {
        const opVpfNum = parseFloat(opVpfRaw);
        if (isNaN(opVpfNum)) {
          newErrors.opening_vpf_balance = "Opening VPF Balance must be a valid number";
        } else if (opVpfNum < 0) {
          newErrors.opening_vpf_balance = "Opening VPF Balance cannot be negative.";
        }
      }

      const opPfNumVal = parseFloat(opPfRaw || 0);
      const opVpfNumVal = parseFloat(opVpfRaw || 0);
      if ((opPfNumVal > 0 || opVpfNumVal > 0) && !formData.opening_pf_balance_as_on) {
        newErrors.opening_pf_balance_as_on = "Opening PF Balance As On is required when Opening PF Balance or Opening VPF Balance is greater than zero.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const generateDefaultEmployeeCode = async () => {
      if (!isOpen || editEmployee) return;

      const nextCode = await fetchNextEmployeeCode("permanent");

      if (nextCode) {
        setFormData((prev) => ({
          ...prev,
          employee_type: "permanent",
          emp_code: nextCode,
        }));
      }
    };

    generateDefaultEmployeeCode();
  }, [isOpen, editEmployee]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError("Please correct the validation errors before submitting.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      const payload = buildEmployeeMasterPayload(
        formData,
        masters,
        departments,
      );

      if (editEmployee) {
        const employeeId = editEmployee.id || editEmployee.employee_id;
        if (!employeeId) {
          setError(
            "Cannot update: employee ID is missing. Close and reopen the edit form.",
          );
          setLoading(false);
          return;
        }
        const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}`), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok) {
          const msg = result.pending_profile_approval
            ? "Critical changes submitted for approval."
            : "Employee updated successfully!";
          setSuccess(msg);
          setTimeout(() => {
            onSuccess?.(result);
            onClose();
          }, 1500);
        } else if (response.status === 422 && Array.isArray(result.detail)) {
          const validationErrors = {};
          result.detail.forEach((err) => {
            if (err.loc?.length > 1) {
              validationErrors[err.loc[err.loc.length - 1]] =
                err.msg || "Invalid value";
            }
          });
          setErrors(validationErrors);
          setError("Please correct the validation errors below.");
        } else {
          setError(result.detail || "Failed to update employee");
        }
        return;
      }

      const response = await fetch(buildApiUrl("/api/v1/employee-master/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const employeeId = result.id;

        if (
          pendingDocuments.length > 0 ||
          formData.other_documents.length > 0
        ) {
          await uploadPendingDocuments(
            employeeId,
            pendingDocuments,
            formData.other_documents,
            token,
          );
        }

        if (
          formData.submit_for_approval &&
          result.workflow_status === "draft"
        ) {
          await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/submit`), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ remarks: "Submitted from onboarding form" }),
          });
        }

        setSuccess("Employee created successfully!");
        setTimeout(() => {
          onSuccess?.(result);
          onClose();
        }, 1500);
      } else {
        const errorData = await response.json();
        if (
          response.status === 422 &&
          errorData.detail &&
          Array.isArray(errorData.detail)
        ) {
          const validationErrors = {};
          errorData.detail.forEach((err) => {
            if (err.loc && err.loc.length > 1) {
              validationErrors[err.loc[err.loc.length - 1]] =
                err.msg || "Invalid value";
            }
          });
          setErrors(validationErrors);
          setError("Please correct the validation errors below.");
        } else {
          setError(
            errorData.detail || errorData.message || "Failed to save employee",
          );
        }
      }
    } catch (err) {
      console.error("Error saving employee:", err);
      setError("Network error occurred while saving employee");
    } finally {
      setLoading(false);
    }
  };

  const addPendingDocument = (documentTypeId, file) => {
    if (!documentTypeId || !file) return;
    // Validate that document_type_id is a valid number
    const numericId = Number(documentTypeId);
    if (isNaN(numericId) || numericId <= 0) {
      console.error("Invalid document_type_id:", documentTypeId);
      setError("Invalid document type selected");
      return;
    }
    setPendingDocuments((prev) => {
      const filtered = prev.filter(
        (p) => Number(p.document_type_id) !== numericId,
      );
      return [
        ...filtered,
        { document_type_id: numericId, file, file_name: file.name },
      ];
    });
  };

  const addOtherDocument = () => {
    setFormData((prev) => ({
      ...prev,
      other_documents: [
        ...prev.other_documents,
        { id: Date.now(), name: "", file: null },
      ],
    }));
  };

  const removeOtherDocument = (id) => {
    setFormData((prev) => ({
      ...prev,
      other_documents: prev.other_documents.filter((doc) => doc.id !== id),
    }));
  };

  const updateOtherDocument = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      other_documents: prev.other_documents.map((doc) =>
        doc.id === id ? { ...doc, [field]: value } : doc,
      ),
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
            <div className="flex items-center text-white">
              <User className="w-6 h-6 mr-3" />
              <div>
                <h2 className="text-xl font-semibold">
                  {viewMode
                    ? "View Employee Details"
                    : editEmployee
                      ? "Edit Employee"
                      : "Add New Employee"}
                </h2>
                <p className="text-sm text-blue-100">
                  {editEmployee
                    ? "Update employee information"
                    : "Fill in all required fields to create a new employee"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!viewMode && (
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                  title={previewMode ? "Exit Preview" : "Preview Form"}
                >
                  {previewMode ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Error/Success Messages */}
          <AnimatePresence>
            {(error || success) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`mx-4 mt-4 p-4 rounded-lg border ${
                  error
                    ? "bg-red-50 border-red-200"
                    : "bg-green-50 border-green-200"
                }`}
              >
                <div className="flex items-center">
                  {error ? (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  ) : (
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                  )}
                  <p className={error ? "text-red-700" : "text-green-700"}>
                    {error || success}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Basic Information Section */}
            <SectionHeader
              title="Basic Information"
              icon={User}
              color="bg-blue-600"
              section="basic"
              expanded={expandedSections.basic}
              onToggle={toggleSection}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Employee Type - Main Selection */}
                {/* <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {employeeTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleEmployeeTypeChange(option.value)}
                        disabled={previewMode}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${formData.employee_type === option.value
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          } ${previewMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{option.label}</span>
                          {formData.employee_type === option.value && (
                            <Check className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {errors.employee_type && (
                    <p className="text-red-600 text-sm flex items-center mt-2">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.employee_type}
                    </p>
                  )}
                </div> */}

                {/* Employee Code - Auto-generated */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Employee Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.emp_code || ""}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Auto-generated"
                  />
                  <p className="text-xs text-gray-500">
                    Auto-generated employee code
                  </p>
                </div>

                <FormField
                  label="First Name"
                  field="first_name"
                  required
                  placeholder="Enter first name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Middle Name"
                  field="middle_name"
                  required
                  placeholder="Enter middle name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Last Name"
                  field="last_name"
                  required
                  placeholder="Enter last name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Official Email"
                  field="official_email"
                  type="email"
                  placeholder="employee@hrmspcb.com"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Personal Email"
                  field="personal_email"
                  type="email"
                  required
                  placeholder="personal@email.com"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Mobile Number"
                  field="phone"
                  type="tel"
                  required
                  placeholder="9876543210"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Alternate Mobile Number"
                  field="alternate_phone"
                  type="tel"
                  required
                  placeholder="9876543210"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
              </div>
            </SectionHeader>

            {/* Organization Details Section */}
            <SectionHeader
              title="Organization Details"
              icon={Building}
              color="bg-green-600"
              section="organization"
              expanded={expandedSections.organization}
              onToggle={toggleSection}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField
                  label="Join Date"
                  field="join_date"
                  type="date"
                  required
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Department"
                  field="department_id"
                  required
                  options={departments.map((department) => ({
                    value: department.id,
                    label: department.name,
                  }))}
                  formData={formData}
                  errors={errors}
                  onChange={(_, value) => handleDepartmentChange(value)}
                  previewMode={previewMode}
                />
                <FormField
                  label="Head (Payroll Head)"
                  field="payroll_head_id"
                  required
                  options={departmentHeadsList}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                  disabled={!formData.department_id || departmentHeadsList.length === 0}
                  helpText="Filtered by Department"
                />
                <FormField
                  label="Designation Type"
                  field="designation_type"
                  required
                  options={designationTypeOptions}
                  formData={formData}
                  errors={errors}
                  onChange={(_, value) => handleDesignationTypeChange(value)}
                  previewMode={previewMode}
                />
                <div className="md:col-span-2">
                  <SearchableSelect
                    label="Designation"
                    value={formData.designation_id}
                    onChange={handleDesignationChange}
                    options={designationOptions}
                    required
                    disabled={
                      previewMode ||
                      mastersLoading ||
                      !formData.department_id ||
                      !formData.designation_type
                    }
                    error={errors.designation_id}
                    helpText="From designation master — filtered by department and designation type"
                  />
                </div>
                <SearchableSelect
                  label="Scale"
                  value={formData.scale_id || ""}
                  onChange={(v) => {
                    handleInputChange("scale_id", v);
                    const sObj = scaleOptions.find((s) => String(s.value) === String(v));
                    if (sObj) {
                      const minB = sObj.min_basic ?? 0;
                      const maxB = sObj.max_basic ?? 0;
                      const formattedScale = `${sObj.code || sObj.name} ( ${minB} - ${maxB} )`;
                      handleInputChange("scale", formattedScale);
                      handleInputChange("pay_scale", formattedScale);
                    }
                  }}
                  options={scaleOptions}
                  disabled={previewMode}
                  error={errors.scale_id}
                  helpText="Select scale from Scale Master"
                />
                {Boolean(formData.scale_id) && (
                  <FormField
                    label="Enter Basic"
                    field="basic_pay"
                    type="number"
                    required
                    placeholder="Enter basic pay"
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    previewMode={previewMode}
                    helpText={basicRangeHelpText}
                  />
                )}
                <SearchableSelect
                  label="Reporting Manager"
                  value={formData.reporting_manager_id}
                  onChange={(v) => handleInputChange("reporting_manager_id", v)}
                  options={managerOptions}
                  disabled={previewMode}
                  onSearch={loadManagers}
                  helpText="Search by name or employee code"
                />
                <SearchableSelect
                  label="Supervisor"
                  value={formData.supervisor_id}
                  onChange={(v) => handleInputChange("supervisor_id", v)}
                  options={managerOptions}
                  disabled={previewMode}
                  onSearch={loadManagers}
                />
                <FormField
                  label="Office Location"
                  field="office_location_id"
                  required
                  options={(masters?.officeLocations || []).map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Default Shift"
                  field="shift_id"
                  required
                  options={(masters?.shifts || []).map((s) => ({
                    value: s.value,
                    label: s.label,
                  }))}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
              </div>

              {/* Allowances section */}
              <div className="mt-6 border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Allowances Eligibility</h4>
                <div className="flex flex-col sm:flex-row gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={!!formData.is_eligible_washing_allowance}
                      onChange={(e) => handleInputChange("is_eligible_washing_allowance", e.target.checked)}
                      disabled={previewMode}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm text-gray-700">Washing Allowance</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={!!formData.is_eligible_family_planning_allowance}
                      onChange={(e) => handleInputChange("is_eligible_family_planning_allowance", e.target.checked)}
                      disabled={previewMode}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm text-gray-700">Family Planning Allowance</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={!!formData.is_eligible_conveyance_allowance}
                      onChange={(e) => handleInputChange("is_eligible_conveyance_allowance", e.target.checked)}
                      disabled={previewMode}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm text-gray-700">Conveyance Eligible</span>
                  </label>
                </div>
              </div>
            </SectionHeader>

            {/* <SectionHeader title="Shift, Roster & Policies" icon={Users} color="bg-teal-600" section="orgGroups" expanded={expandedSections.orgGroups} onToggle={toggleSection}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField label="Shift Group" field="shift_group_id" options={(masters?.shiftGroups || []).map((g) => ({ value: g.id, label: g.name }))} formData={formData} errors={errors} onChange={handleInputChange} previewMode={previewMode} />
                <FormField label="Roster Group" field="roster_group_id" options={(masters?.rosterGroups || []).map((g) => ({ value: g.id, label: g.name }))} formData={formData} errors={errors} onChange={handleInputChange} previewMode={previewMode} />
                <FormField label="Payroll Group" field="payroll_group_id" options={(masters?.payrollGroups || []).map((g) => ({ value: g.id, label: g.name }))} formData={formData} errors={errors} onChange={handleInputChange} previewMode={previewMode} />
                <FormField label="Leave Policy" field="leave_policy_master_id" options={(masters?.leavePolicies || []).map((g) => ({ value: g.id, label: g.name }))} formData={formData} errors={errors} onChange={handleInputChange} previewMode={previewMode} />
                <FormField label="Attendance Policy" field="attendance_policy_master_id" options={(masters?.attendancePolicies || []).map((g) => ({ value: g.id, label: g.name }))} formData={formData} errors={errors} onChange={handleInputChange} previewMode={previewMode} />
                <FormField
                  label="Employment Status"
                  field="employment_status"
                  options={[
                    { value: 'probation', label: 'Probation' },
                    { value: 'active', label: 'Active' },
                    { value: 'confirmed', label: 'Confirmed' },
                  ]}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
              </div>
            </SectionHeader> */}

            {/* Personal Information Section */}
            <SectionHeader
              title="Personal Information"
              icon={User}
              color="bg-purple-600"
              section="personal"
              expanded={expandedSections.personal}
              onToggle={toggleSection}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField
                  label="Father's Full Name"
                  field="father_name"
                  required
                  placeholder="Enter father's full name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Mother's Full Name"
                  field="mother_name"
                  required
                  placeholder="Enter mother's full name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Date of Birth"
                  field="date_of_birth"
                  type="date"
                  required
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Gender"
                  field="sex"
                  required
                  options={[
                    { value: "M", label: "Male (M)" },
                    { value: "F", label: "Female (F)" },
                  ]}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Marital Status"
                  field="marital_status"
                  required
                  options={[
                    { value: "single", label: "Single" },
                    { value: "married", label: "Married" },
                    { value: "divorced", label: "Divorced" },
                    { value: "widowed", label: "Widowed" },
                  ]}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Blood Group"
                  field="blood_group"
                  required
                  options={[
                    { value: "A+", label: "A+" },
                    { value: "A-", label: "A-" },
                    { value: "B+", label: "B+" },
                    { value: "B-", label: "B-" },
                    { value: "AB+", label: "AB+" },
                    { value: "AB-", label: "AB-" },
                    { value: "O+", label: "O+" },
                    { value: "O-", label: "O-" },
                  ]}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Person with Disability{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  {previewMode ? (
                    <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 min-h-[38px] flex items-center">
                      {formData.is_disabled === "Yes" ? (
                        "Yes"
                      ) : formData.is_disabled === "No" ? (
                        "No"
                      ) : (
                        <span className="text-gray-400">Not provided</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="is_disabled"
                          value="Yes"
                          checked={formData.is_disabled === "Yes"}
                          onChange={(e) =>
                            handleInputChange("is_disabled", e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="is_disabled"
                          value="No"
                          checked={formData.is_disabled === "No"}
                          onChange={(e) =>
                            handleInputChange("is_disabled", e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                    </div>
                  )}
                  {errors.is_disabled && (
                    <p className="text-red-600 text-sm flex items-center mt-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.is_disabled}
                    </p>
                  )}
                </div>
              </div>
            </SectionHeader>

            {/* Address & Contact Section */}
            <SectionHeader
              title="Address & Contact"
              icon={MapPin}
              color="bg-orange-600"
              section="address"
              expanded={expandedSections.address}
              onToggle={toggleSection}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField
                    label="Current Address"
                    field="current_address"
                    required
                    rows={2}
                    placeholder="Enter complete current address"
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    previewMode={previewMode}
                  />
                </div>
                <div className="md:col-span-2">
                  <FormField
                    label="Permanent Address"
                    field="permanent_address"
                    required
                    rows={2}
                    placeholder="Enter complete permanent address"
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    previewMode={previewMode}
                  />
                </div>
                <FormField
                  label="City"
                  field="city"
                  required
                  placeholder="Enter city name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="State"
                  field="state"
                  required
                  placeholder="Enter state name"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="PIN Code"
                  field="pin_code"
                  required
                  placeholder="123456"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Emergency Contact Name"
                  field="emergency_contact_name"
                  required
                  placeholder="Contact person"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Emergency Contact Number"
                  field="emergency_contact_number"
                  required
                  placeholder="10-digit mobile"
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
                <FormField
                  label="Relationship"
                  field="emergency_contact_relationship"
                  required
                  options={[
                    { value: "Father", label: "Father" },
                    { value: "Mother", label: "Mother" },
                    { value: "Spouse", label: "Spouse" },
                    { value: "Son", label: "Son" },
                    { value: "Daughter", label: "Daughter" },
                    { value: "Brother", label: "Brother" },
                    { value: "Sister", label: "Sister" },
                    { value: "Guardian", label: "Guardian" },
                    { value: "Friend", label: "Friend" },
                    { value: "Relative", label: "Relative" },
                    { value: "Other", label: "Other" },
                  ]}
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  previewMode={previewMode}
                />
              </div>
            </SectionHeader>

            {/* Bank & Tax Details Section */}
            <SectionHeader
              title="Bank & Tax Details"
              icon={CreditCard}
              color="bg-indigo-600"
              section="bank"
              expanded={expandedSections.bank}
              onToggle={toggleSection}
            >
              <div className="space-y-6">
                {/* Tax & Identity */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Tax & Identity Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="PAN Number"
                      field="pan_number"
                      placeholder="ABCDE1234F"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                    <FormField
                      label="Aadhaar Number"
                      field="aadhaar_number"
                      required
                      placeholder="123456789012"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                    <FormField
                      label="Pension Scheme Type"
                      field="pension_scheme_type"
                      required
                      options={[
                        {
                          value: "GPF",
                          label: "GPF (Government Provident Fund)",
                        },
                        { value: "NPS", label: "NPS (New Pension Scheme)" },
                      ]}
                      formData={formData}
                      errors={errors}
                      onChange={(_, value) => handlePensionSchemeChange(value)}
                      previewMode={previewMode}
                    />
                    {formData.pension_scheme_type === "GPF" && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Employee Code
                        </label>
                        <input
                          type="text"
                          value={formData.emp_code || ""}
                          readOnly
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                          placeholder="Auto-generated from Basic Information"
                        />
                        <p className="text-xs text-gray-500">
                          Employee Code from Basic Information
                        </p>
                      </div>
                    )}
                    {formData.pension_scheme_type === "NPS" && (
                      <FormField
                        label="PRAN Number"
                        field="pran_number"
                        placeholder="PRAN Number"
                        formData={formData}
                        errors={errors}
                        onChange={handleInputChange}
                        previewMode={previewMode}
                      />
                    )}
                  </div>

                  {/* Opening PF Balance Section */}
                  {formData.pension_scheme_type === "GPF" && (
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-1 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2 text-blue-600" />
                      Opening PF Balance
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Enter accumulated PF/VPF balance available before onboarding into HRMS.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                      <FormField
                        label="Opening PF Balance (₹)"
                        field="opening_pf_balance"
                        type="number"
                        placeholder="0.00"
                        formData={formData}
                        errors={errors}
                        onChange={handleInputChange}
                        previewMode={previewMode || isPfAccountInitialized}
                        disabled={isPfAccountInitialized}
                        helpText={isPfAccountInitialized ? "Read-only: PF Account already initialized" : "Employee contribution opening balance"}
                      />
                      <FormField
                        label="Opening VPF Balance (₹)"
                        field="opening_vpf_balance"
                        type="number"
                        placeholder="0.00"
                        formData={formData}
                        errors={errors}
                        onChange={handleInputChange}
                        previewMode={previewMode || isPfAccountInitialized}
                        disabled={isPfAccountInitialized}
                        helpText={isPfAccountInitialized ? "Read-only: PF Account already initialized" : "Voluntary PF opening balance"}
                      />
                      <FormField
                        label="Opening Balance As On"
                        field="opening_pf_balance_as_on"
                        type="date"
                        formData={formData}
                        errors={errors}
                        onChange={handleInputChange}
                        previewMode={previewMode || isPfAccountInitialized}
                        disabled={isPfAccountInitialized}
                        helpText="Required if Opening PF/VPF > 0"
                      />
                    </div>
                  </div>
                  )}
                </div>

                {/* Bank Account */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Building className="w-4 h-4 mr-2" />
                    Bank Account Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-gray-600 uppercase mb-1">
                        Bank Name <span className="text-red-500">*</span>
                      </label>
                      {previewMode ? (
                        <div className="p-2.5 bg-gray-50 border rounded-lg text-sm text-gray-800 font-medium">
                          {formData.bank_name || "None"}
                        </div>
                      ) : (
                        <select
                          name="bank_id"
                          value={formData.bank_id || ""}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const bankObj = banksList.find(b => String(b.id) === String(selectedId));
                            setFormData(prev => ({
                              ...prev,
                              bank_id: selectedId ? parseInt(selectedId) : "",
                              bank_name: bankObj ? bankObj.bank_name : ""
                            }));
                          }}
                          className={`p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm ${errors.bank_id ? 'border-red-500' : 'border-gray-300'}`}
                        >
                          <option value="">Select Bank</option>
                          {banksList.map(bank => (
                            <option key={bank.id} value={bank.id}>
                              {bank.bank_name}
                            </option>
                          ))}
                        </select>
                      )}
                      {errors.bank_id && (
                        <span className="text-xs text-red-500 mt-1 font-medium">{errors.bank_id}</span>
                      )}
                    </div>
                    <FormField
                      label="Account Number"
                      field="bank_account_number"
                      required
                      placeholder="Bank account number"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                    <FormField
                      label="IFSC Code"
                      field="bank_ifsc_code"
                      required
                      placeholder="SBIN0001234"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                    <FormField
                      label="Branch"
                      field="bank_branch"
                      required
                      placeholder="e.g., Kolkata Main Branch"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                    <FormField
                      label="Branch Code"
                      field="bank_branch_code"
                      required
                      placeholder="e.g., 001234"
                      formData={formData}
                      errors={errors}
                      onChange={handleInputChange}
                      previewMode={previewMode}
                    />
                  </div>
                </div>
              </div>
            </SectionHeader>

            {!editEmployee && (
              <SectionHeader
                title="Onboarding Documents"
                icon={Upload}
                color="bg-amber-600"
                section="documents"
                expanded={expandedSections.documents}
                onToggle={toggleSection}
              >
                {!masters?.documentTypes ||
                masters.documentTypes.length === 0 ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      Loading document types... Please wait.
                    </p>
                  </div>
                ) : (
                  <>
                    {errors.documents && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{errors.documents}</p>
                      </div>
                    )}
                    {docChecklist && (
                      <div
                        className={`mb-4 p-4 rounded-lg border ${docChecklist.complete ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}
                      >
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-2">
                            Required Documents
                          </p>
                          <ul className="text-sm space-y-1">
                            {docChecklist.items.map((item) => (
                              <li
                                key={item.document_type_id}
                                className="flex items-center gap-2"
                              >
                                {item.uploaded ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-amber-600" />
                                )}
                                {item.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="pt-3 border-t border-gray-200">
                          <p className="text-sm font-semibold text-gray-700">
                            Progress
                          </p>
                          <p className="text-sm text-gray-600">
                            {
                              docChecklist.items.filter((i) => i.uploaded)
                                .length
                            }{" "}
                            / {docChecklist.items.length} Uploaded
                          </p>
                          <p className="text-sm text-gray-600">
                            {Math.round(
                              (docChecklist.items.filter((i) => i.uploaded)
                                .length /
                                docChecklist.items.length) *
                                100,
                            )}
                            %
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {mandatoryDocTypes.map((docType) => {
                        const pending = pendingDocuments.find(
                          (p) =>
                            Number(p.document_type_id) ===
                            Number(docType.value),
                        );
                        return (
                          <div
                            key={docType.value}
                            className="flex flex-wrap items-center gap-3 p-3 border rounded-lg"
                          >
                            <span className="text-sm font-medium flex-1">
                              {docType.label}
                              {docType.mandatory && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </span>
                            {pending && (
                              <span className="text-xs text-green-700">
                                {pending.file_name}
                              </span>
                            )}
                            {!previewMode && (
                              <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm cursor-pointer">
                                <Upload className="w-4 h-4" />
                                {pending ? "Replace" : "Upload"}
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) =>
                                    addPendingDocument(
                                      docType.value,
                                      e.target.files?.[0],
                                    )
                                  }
                                />
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Other Documents Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Other Documents
                  </h4>
                  <div className="space-y-3">
                    {formData.other_documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex flex-wrap items-center gap-3 p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex-1 min-w-[200px]">
                          <input
                            type="text"
                            placeholder="Document Name"
                            value={doc.name}
                            onChange={(e) =>
                              updateOtherDocument(
                                doc.id,
                                "name",
                                e.target.value,
                              )
                            }
                            disabled={previewMode}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          {doc.file ? (
                            <span className="text-xs text-green-700">
                              {doc.file.name}
                            </span>
                          ) : (
                            !previewMode && (
                              <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm cursor-pointer">
                                <Upload className="w-4 h-4" />
                                Upload
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) =>
                                    updateOtherDocument(
                                      doc.id,
                                      "file",
                                      e.target.files?.[0],
                                    )
                                  }
                                />
                              </label>
                            )
                          )}
                        </div>
                        {!previewMode && (
                          <button
                            type="button"
                            onClick={() => removeOtherDocument(doc.id)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {!previewMode && (
                      <button
                        type="button"
                        onClick={addOtherDocument}
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
                      >
                        + Add Another Document
                      </button>
                    )}
                  </div>
                </div>
              </SectionHeader>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {formData.employee_type && (
                <span className="flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  Type:{" "}
                  {employeeTypeOptions.find(
                    (o) => o.value === formData.employee_type,
                  )?.label || displayEmployeeType(formData.employee_type)}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {!editEmployee && !viewMode && (
                <label className="flex items-center gap-2 text-sm text-gray-700 mr-4">
                  <input
                    type="checkbox"
                    checked={formData.submit_for_approval}
                    onChange={(e) =>
                      handleInputChange("submit_for_approval", e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600"
                  />
                  Submit for approval after create
                </label>
              )}
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              {!viewMode && (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editEmployee ? "Update Employee" : "Create Employee"}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmployeeCreationForm;
